import { Injectable, inject } from '@angular/core'

import { combine, createStore, merge, restore, sample } from 'effector'
import { configure } from 'effector-logger'
import { produce } from 'immer'
import { and } from 'patronum'

import { Observable, from } from 'rxjs'

import { CanvasData, RenderableNode } from '@flowx/designer/modules/bpmn-editor/bpmn-canvas.types'
import { BpmnMouseEvent } from '@flowx/designer/modules/bpmn-editor/bpmn-editor.types'
import { BpmnGraphUtil } from '@flowx/designer/modules/bpmn-editor/bpmn-graph.util'
import { BpmnEditorEffects } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.effects'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { NodeDefinitionNormalized } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import { PanelSize } from '@flowx/designer/modules/process-details/process-details-helper.service'
import { AppEvents } from '@flowx/designer/shared/effector/app/app.events'
import { AreasEvents } from '@flowx/designer/shared/effector/area/area.events'
import { AreaStores } from '@flowx/designer/shared/effector/area/area.stores'
import { BoundaryEventStores } from '@flowx/designer/shared/effector/boundary-event/boundary-event.stores'
import { BpmnBoundaryEventEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-boundary-event.events'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { BpmnNodeEvents } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-node.events'
import { NodeStores } from '@flowx/designer/shared/effector/node/node.stores'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import { ProcessEffects } from '@flowx/designer/shared/effector/process/process.effects'
import { SwimlaneStores } from '@flowx/designer/shared/effector/swimlane/swimlane.stores'
import { BoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/boundary-event.types'
import { Area } from '@flowx/designer/shared/entities/nav-area.entity'

import { BpmnEditorEvents } from './bpmn-editor.events'

import { EFFECTOR_TOKEN } from '../../../shared/angular-effector'

@Injectable({ providedIn: 'root' })
export class BpmnEditorStore {
  readonly #Effector = inject(EFFECTOR_TOKEN)
  readonly areasStore = inject(AreaStores)
  readonly bpmnEditorEffects = inject(BpmnEditorEffects)

  // INIT

  readonly editable$ = combine([
    BpmnEditorStores.readonly$,
    ProcessEffector.processDefinition$,
  ]).map(([readonly, processVersion]) => (processVersion?.deleted ? false : !readonly))

  readonly canvasZoom$ = restore(BpmnEditorEvents.canvasZoom, { zoom: 1 })
  readonly canvasTransformation$ = this.#Effector
    .store<number[]>(null, {
      name: 'canvasTransformation$',
    })
    .on(BpmnEditorEvents.canvasViewportTransform, (_, transform) => transform)
  readonly canvasFocus$ = this.#Effector
    .createStore<boolean>(false, { name: 'canvasFocus$' })
    .on(BpmnEditorEvents.canvasFocus, (_, focus) => focus)

  readonly showAreas$ = this.#Effector
    .createStore<boolean>(false, { name: 'showAreas$' })
    .on(BpmnEditorEvents.showAreasChange, (_, showAreas) => showAreas)

  readonly aiNodes$ = this.#Effector
    .store<Map<RenderableNode['id'], RenderableNode>>(new Map(), {
      name: 'aiNodes$',
    })
    .on(BpmnEditorEvents.renderAiNodesMetadata, (_, { aiNodes }) => {
      const nodes = new Map<RenderableNode['id'], RenderableNode>()

      aiNodes.forEach((node) => {
        nodes.set(node.id, node)
      })

      return nodes
    })
    .reset([
      BpmnEditorEvents.generatedAiNodesSuccess,
      ProcessEffects.bulkUpdateProcessEntitiesFx.done,
    ])

  readonly aiDeletedNodes$ = this.#Effector
    .store<Set<NodeDefinitionNormalized['id']>>(new Set(), {
      name: 'aiDeletedNodes$',
    })
    .on(BpmnEditorEvents.renderAiNodesMetadata, (_, response) => {
      const nodes = new Set<NodeDefinitionNormalized['id']>()
      response.deletedNodes.forEach((nodeId) => {
        nodes.add(nodeId)
      })
      return nodes
    })
    .reset([
      BpmnEditorEvents.generatedAiNodesSuccess,
      ProcessEffects.bulkUpdateProcessEntitiesFx.done,
    ])

  readonly draftNodes$ = this.#Effector
    .store<Map<NodeDefinitionNormalized['id'], Pick<NodeDefinitionNormalized, 'layoutOptions'>>>(
      new Map(),
      {
        name: 'draftNodes$',
      }
    )
    .on(BpmnEditorEvents.renderAiNodesMetadata, (state, { updatedNodes }) => {
      const newDraftNodes = new Map(state)
      updatedNodes.forEach(({ id, layoutOptions }) => {
        newDraftNodes.set(id, { layoutOptions })
      })
      return newDraftNodes
    })
    .reset(ProcessEffects.bulkUpdateProcessEntitiesFx.done)

  readonly allNodes$ = combine({
    aiNodes: this.aiNodes$,
    bpmnNodes: NodeStores.nodeDefinitions$,
    draftNodes: this.draftNodes$,
    deletedNodes: this.aiDeletedNodes$,
  }).map(({ aiNodes, bpmnNodes, draftNodes, deletedNodes }) => {
    const allNodes = new Map<RenderableNode['id'], RenderableNode>()

    bpmnNodes.forEach((node) => {
      if (deletedNodes.has(node.id)) {
        return
      }

      const draftSequence = draftNodes.get(node.id)
      const updatedSequence = produce(node, (draft) => {
        draft.layoutOptions = draftSequence?.layoutOptions ?? node.layoutOptions
      })
      allNodes.set(node.id, updatedSequence)
    })

    aiNodes.forEach((node) => {
      allNodes.set(node.id, node)
    })

    return allNodes
  })

  readonly processIsPristine$ = combine({
    nodes: this.allNodes$,
    swimlanes: SwimlaneStores.swimlaneSummaries$,
  }).map(({ nodes, swimlanes }) => nodes.size === 0 && swimlanes.size === 1)

  readonly aiCtaLayoutOptions$ = combine({
    swimlanes: SwimlaneStores.swimlaneSummaries$,
    canvasTransform: this.canvasTransformation$,
    zoom: this.canvasZoom$,
  }).map(({ swimlanes, canvasTransform, zoom }) => {
    const firstSwimlane = [...swimlanes.values()][0]

    if (!firstSwimlane || !canvasTransform) {
      return null
    }

    const {
      layoutOptions: { x, y, width, height },
    } = firstSwimlane

    const anchorPoint = BpmnGraphUtil.transformPoint({ x, y }, canvasTransform)
    const oppositeCornerPoint = BpmnGraphUtil.transformPoint(
      { x: x + width, y: y + height },
      canvasTransform
    )

    return {
      containerTransform: `translate(${anchorPoint.x}px, ${anchorPoint.y}px)`,
      contentTransform: `scale(${zoom.zoom})`,
      width: oppositeCornerPoint.x - anchorPoint.x,
      height: oppositeCornerPoint.y - anchorPoint.y,
    }
  })

  readonly aiEmptyPreviewBoxLayoutOptions = combine({
    swimlanes: SwimlaneStores.swimlaneSummaries$,
    canvasTransform: this.canvasTransformation$,
    zoom: this.canvasZoom$,
  }).map(({ swimlanes, canvasTransform, zoom }) => {
    const firstSwimlane = [...swimlanes.values()][0]
    const padding = 20

    if (!firstSwimlane || !canvasTransform) {
      return null
    }

    const {
      layoutOptions: { x, y, width, height },
    } = firstSwimlane

    const anchorPoint = BpmnGraphUtil.transformPoint(
      { x: x + padding, y: y + padding },
      canvasTransform
    )
    const oppositeCornerPoint = BpmnGraphUtil.transformPoint(
      { x: x + width - padding, y: y + height - padding },
      canvasTransform
    )

    return {
      x: x + padding,
      y: y + padding,
      width: (oppositeCornerPoint.x - anchorPoint.x) * (1 / zoom.zoom),
      height: (oppositeCornerPoint.y - anchorPoint.y) * (1 / zoom.zoom),
    }
  })

  readonly data$ = this.#Effector
    .createStore<CanvasData & { processId: ProcessDefinition['id'] }>(
      { swimlanes: [], nodes: [], sequences: [], boundaryEvents: [], processId: null },
      { name: 'data$' }
    )
    .on(BpmnEditorEvents.dataChange, (_, data) => data)

  readonly lastSwimlaneClickEvent$ = createStore<BpmnMouseEvent<SwimlaneSummary>>(null, {
    name: 'lastSwimlaneClickEvent$',
  })
    .on(BpmnEditorEvents.swimlaneLeftClick, (_, clickEvent) => clickEvent)
    .on(BpmnEditorEvents.swimlaneRightClick, (_, clickEvent) => clickEvent)
    .on([AppEvents.processDiagramDestroy, BpmnEditorEvents.processVersionChanged], () => null)

  readonly areasSelectedSwimlane$ = this.#Effector
    .createStore<string>(null, {
      name: 'areasSelectedSwimlane$',
    })
    .on(this.areasStore.areas$, (state, areas) => {
      if (!state) {
        return areas.keys().next().value ?? null
      }
    })
    .on(
      merge([BpmnEditorStores.selectedNode$, this.lastSwimlaneClickEvent$]),
      (state, selectedEntity: any) =>
        selectedEntity?.swimlaneDefinitionId ?? selectedEntity?.entity?.id ?? state
    )
    .on(BpmnEditorEvents.areasSelectedSwimlaneChange, (_, swimlaneId) => swimlaneId)
    .on(AreasEvents.cleanupAreas, () => null)

  readonly areaBySelectedSwimlane$ = combine(
    this.areasStore.areas$,
    this.areasSelectedSwimlane$,
    (areas, areasSelectedSwimlane) => areas.get(areasSelectedSwimlane)?.navigationTree ?? null
  )

  areaBySelectedSwimlaneObservable$ = from(
    this.areaBySelectedSwimlane$ as unknown as Observable<Area[]>
  )

  readonly viewportsBySelectedSwimlane$ = combine(
    this.areasStore.areas$,
    this.areasSelectedSwimlane$,
    (areas, areasSelectedSwimlane) => areas.get(areasSelectedSwimlane)?.userTasks ?? null
  )
  readonly navigationAreasSelectedSwimlane$ = combine(
    SwimlaneStores.swimlaneSummaries$,
    this.areasSelectedSwimlane$,
    (swimlanes, areasSelectedSwimlane) => swimlanes.get(areasSelectedSwimlane) ?? null
  )

  // GENERAL
  readonly panelSize$ = createStore<PanelSize>(PanelSize.SMALL, {
    name: 'panelSize$',
  }).on(BpmnEditorEvents.panelSizeChangeFromProcessDetailsHelper, (_, panelSize) => panelSize)

  readonly selectedBoundaryEventId$ = createStore<BoundaryEvent['id']>(null, {
    name: 'selectedBoundaryEvent$',
  })
    .on(BpmnBoundaryEventEvents.boundaryEventSelectedByUrl, (_, id) => id)
    .on(
      [BpmnBoundaryEventEvents.boundaryLeftClick, BpmnBoundaryEventEvents.boundaryRightClick],
      (_, payload) => payload?.entity?.id
    )
    .on(
      [
        BpmnEditorStores.multipleEntitiesSelected$,
        BpmnNodeEvents.nodeLeftClick,
        BpmnEditorEvents.processVersionChanged,
        BpmnEditorEvents.boundaryEventDeselected,
        this.bpmnEditorEffects.boundaryEventRemoveFx.done,
      ],
      () => null
    )

  readonly selectedBoundaryEvent$ = combine({
    id: this.selectedBoundaryEventId$,
    boundaryEvents: BoundaryEventStores.$boundaryEvents,
  }).map(({ id, boundaryEvents }) => boundaryEvents.get(id) ?? null)

  readonly nodeOrBoundaryEventSelected$ = combine([
    BpmnEditorStores.selectedNodeDenormalized$,
    this.selectedBoundaryEvent$,
  ]).map(([node, boundaryEvent]) => node ?? boundaryEvent)

  readonly canvasCaptureKeyboardEvent$ = and(this.canvasFocus$)

  constructor() {
    configure(
      [
        this.canvasZoom$,
        this.canvasTransformation$,
        BpmnEditorEvents.canvasPan,
        BpmnEditorEvents.canvasZoom,
      ],
      {
        log: 'disabled',
      }
    )

    sample({
      clock: this.allNodes$,
      target: BpmnEditorEvents.allNodesStoreUpdates,
    })
  }
}
